import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Select, Input, ButtonGroup, Field } from './UIComponents';
import Icon from '@mdi/react';
import { mdiCalendar, mdiChevronDown, mdiChevronUp } from '@mdi/js';

const QuoteComponentsFilter = ({
  isOpen,
  onToggle,
  filterData = {},
  onFilterChange,
  quotedBy = 'face_amount' // 'face_amount' or 'premium'
}) => {
  const [showTableRatings, setShowTableRatings] = useState(false);
  const [showFlatExtra, setShowFlatExtra] = useState(false);

  const styles = {
    container: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      marginBottom: '20px',
      overflow: 'hidden'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 20px',
      backgroundColor: '#f8f9fa',
      borderBottom: isOpen ? '1px solid #dee2e6' : 'none',
      cursor: 'pointer',
      userSelect: 'none'
    },
    headerTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#333',
      margin: 0
    },
    content: {
      display: isOpen ? 'block' : 'none',
      padding: '20px'
    },
    form: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '20px'
    },
    fieldGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    },
    healthClassGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '8px',
      marginBottom: '12px'
    },
    smokingSection: {
      marginTop: '12px'
    },
    smokingLabel: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#333',
      marginBottom: '8px'
    },
    additionalOptions: {
      marginTop: '12px',
      padding: '12px',
      backgroundColor: '#f8f9fa',
      borderRadius: '4px'
    },
    tableRatingDropdown: {
      marginTop: '8px'
    },
    flatExtraInput: {
      marginTop: '8px'
    },
    faceAmountGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '12px'
    },
    premiumSection: {
      display: 'grid',
      gridTemplateColumns: '1fr 200px',
      gap: '12px',
      alignItems: 'end'
    }
  };

  // Product options based on ALQ implementation
  const productOptions = [
    { value: '', label: 'Select Product Type' },
    { value: 'whole', label: 'Whole Life' },
    { value: 'term', label: 'Term Life' },
    { value: 'iul', label: 'Indexed Universal Life (IUL)' },
    { value: 'ul', label: 'Universal Life (UL)' },
    { value: 'gul', label: 'Guaranteed Universal Life (GUL)' },
    { value: 'vul', label: 'Variable Universal Life (VUL)' }
  ];

  // Table rating options
  const tableRatingOptions = [
    { value: '', label: 'Select Table Rating' },
    { value: 'table_a', label: 'Table A (+25%)' },
    { value: 'table_b', label: 'Table B (+50%)' },
    { value: 'table_c', label: 'Table C (+75%)' },
    { value: 'table_d', label: 'Table D (+100%)' },
    { value: 'table_e', label: 'Table E (+125%)' },
    { value: 'table_f', label: 'Table F (+150%)' },
    { value: 'table_g', label: 'Table G (+175%)' },
    { value: 'table_h', label: 'Table H (+200%)' },
    { value: 'table_i', label: 'Table I (+225%)' },
    { value: 'table_j', label: 'Table J (+250%)' }
  ];

  // Premium mode options
  const premiumModeOptions = [
    { value: 'annual', label: 'Annual' },
    { value: 'semi_annual', label: 'Semi-Annual' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const handleInputChange = (field, value) => {
    onFilterChange({ ...filterData, [field]: value });
  };

  const handleHealthClassChange = healthClass => {
    const currentSelection = filterData.healthClass || [];
    let newSelection;

    if (currentSelection.includes(healthClass)) {
      // Remove if already selected
      newSelection = currentSelection.filter(hc => hc !== healthClass);
    } else if (currentSelection.length < 2) {
      // Add if less than 2 selected
      newSelection = [...currentSelection, healthClass];
    } else {
      // Replace oldest selection if 2 already selected
      newSelection = [currentSelection[1], healthClass];
    }

    handleInputChange('healthClass', newSelection);

    // Handle special cases
    if (healthClass === 'Table Rated') {
      setShowTableRatings(newSelection.includes('Table Rated'));
    }
    if (healthClass === 'Flat Extra') {
      setShowFlatExtra(newSelection.includes('Flat Extra'));
    }
  };

  const isHealthClassSelected = healthClass => {
    return (filterData.healthClass || []).includes(healthClass);
  };

  const isHealthClassDisabled = healthClass => {
    const currentSelection = filterData.healthClass || [];
    return currentSelection.length >= 2 && !currentSelection.includes(healthClass);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header} onClick={onToggle}>
        <h3 style={styles.headerTitle}>Quote Components</h3>
        <Icon path={isOpen ? mdiChevronUp : mdiChevronDown} size={1} color='#6c757d' />
      </div>

      <div style={styles.content}>
        <div style={styles.form}>
          {/* Date of Birth */}
          <div style={styles.fieldGroup}>
            <Field label='Date of Birth'>
              <div style={{ position: 'relative' }}>
                <Input type='date' value={filterData.dateOfBirth || ''} onChange={e => handleInputChange('dateOfBirth', e.target.value)} style={{ paddingRight: '40px' }} placeholder='Select Date' />
                <div
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '16px',
                    color: '#6c757d',
                    pointerEvents: 'none'
                  }}
                >
                  <Icon path={mdiCalendar} size={0.8} />
                </div>
              </div>
            </Field>
          </div>

          {/* Gender */}
          <div style={styles.fieldGroup}>
            <Field label='Gender'>
              <ButtonGroup>
                <Button onClick={() => handleInputChange('gender', 'Male')} variant={filterData.gender === 'Male' ? 'active' : 'default'}>
                  Male{filterData.gender === 'Male' ? ' ✓' : ''}
                </Button>
                <Button onClick={() => handleInputChange('gender', 'Female')} variant={filterData.gender === 'Female' ? 'active' : 'default'}>
                  Female{filterData.gender === 'Female' ? ' ✓' : ''}
                </Button>
              </ButtonGroup>
            </Field>
          </div>

          {/* Product */}
          <div style={styles.fieldGroup}>
            <Field label='Product'>
              <Select value={filterData.product || ''} onChange={e => handleInputChange('product', e.target.value)} options={productOptions} placeholder='Select Product Type' />
            </Field>
          </div>

          {/* Health Class */}
          <div style={styles.fieldGroup}>
            <Field label='Health Class'>
              <div style={styles.healthClassGrid}>
                {['Preferred Plus', 'Preferred', 'Standard Plus', 'Standard', 'Table Rated', 'Flat Extra'].map(healthClass => (
                  <Button
                    key={healthClass}
                    onClick={() => handleHealthClassChange(healthClass)}
                    variant={isHealthClassSelected(healthClass) ? 'active' : 'default'}
                    disabled={isHealthClassDisabled(healthClass)}
                    style={{
                      fontSize: '12px',
                      padding: '6px 8px',
                      opacity: isHealthClassDisabled(healthClass) ? 0.5 : 1
                    }}
                  >
                    {healthClass}
                    {isHealthClassSelected(healthClass) ? ' ✓' : ''}
                  </Button>
                ))}
              </div>

              {/* Smoking/Non-Smoking Section */}
              <div style={styles.smokingSection}>
                <div style={styles.smokingLabel}>Tobacco Use</div>
                <ButtonGroup>
                  <Button onClick={() => handleInputChange('tobaccoUse', 'non-smoking')} variant={filterData.tobaccoUse === 'non-smoking' || !filterData.tobaccoUse ? 'active' : 'default'}>
                    Non-Smoking{filterData.tobaccoUse === 'non-smoking' || !filterData.tobaccoUse ? ' ✓' : ''}
                  </Button>
                  <Button onClick={() => handleInputChange('tobaccoUse', 'smoking')} variant={filterData.tobaccoUse === 'smoking' ? 'active' : 'default'}>
                    Smoking{filterData.tobaccoUse === 'smoking' ? ' ✓' : ''}
                  </Button>
                </ButtonGroup>
              </div>

              {/* Additional Options */}
              <div style={styles.additionalOptions}>
                {/* Table Rating Dropdown */}
                {showTableRatings && (
                  <div style={styles.tableRatingDropdown}>
                    <Field label='Table Rating'>
                      <Select value={filterData.tableRating || ''} onChange={e => handleInputChange('tableRating', e.target.value)} options={tableRatingOptions} placeholder='Select Table Rating' />
                    </Field>
                  </div>
                )}

                {/* Flat Extra Input */}
                {showFlatExtra && (
                  <div style={styles.flatExtraInput}>
                    <Field label='Flat Extra (per $1,000)'>
                      <Input
                        type='number'
                        step='0.01'
                        min='0'
                        placeholder='Enter amount per thousand'
                        value={filterData.flatExtra || ''}
                        onChange={e => handleInputChange('flatExtra', e.target.value)}
                      />
                    </Field>
                  </div>
                )}
              </div>
            </Field>
          </div>

          {/* Face Amount/Premium */}
          <div style={styles.fieldGroup}>
            <Field label='Face Amount/Premium'>
              {quotedBy === 'face_amount' ? (
                <div style={styles.faceAmountGrid}>
                  <Input placeholder='Min Face Amount' value={filterData.faceAmountMin || ''} onChange={e => handleInputChange('faceAmountMin', e.target.value)} />
                  <Input placeholder='Target Face Amount' value={filterData.faceAmountTarget || ''} onChange={e => handleInputChange('faceAmountTarget', e.target.value)} />
                  <Input placeholder='Max Face Amount' value={filterData.faceAmountMax || ''} onChange={e => handleInputChange('faceAmountMax', e.target.value)} />
                </div>
              ) : (
                <div style={styles.premiumSection}>
                  <Input placeholder='Premium Amount' value={filterData.premium || ''} onChange={e => handleInputChange('premium', e.target.value)} />
                  <Select value={filterData.premiumMode || 'annual'} onChange={e => handleInputChange('premiumMode', e.target.value)} options={premiumModeOptions} />
                </div>
              )}
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
};

QuoteComponentsFilter.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  filterData: PropTypes.object,
  onFilterChange: PropTypes.func.isRequired,
  quotedBy: PropTypes.oneOf(['face_amount', 'premium'])
};

export default QuoteComponentsFilter;
